// src/features/help/useHelpTokens.ts

import { useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ExtPlatConfig } from '@/shared/utils/extplatBrandConfig';
import { HELP_SUPPORT_EMAIL } from './helpConstants';
import type { HelpTokens } from './HelpContentUtils';
import { BrandTokens } from './brandTokens';

/** Help tokens plus the platform operator's name (see brandTokens.ts for the two rules). */
export interface BrandTokenValues extends HelpTokens {
  /**
   * Who runs the platform for this account — store submissions, the
   * delegation, the privacy policy's processor sentence. The direct brand for
   * direct accounts AND reseller root accounts; the reseller label only for a
   * reseller's sub-accounts.
   */
  operatorName: string;
}

/**
 * Brand-aware values for {{brandName}} / {{supportContact}} (plan §5.7) and
 * the operator name. Reseller end-customers must never see the direct brand
 * or its support email.
 */
export function useHelpTokens(): BrandTokenValues {
  const { selectedCustomer } = useAuth();
  const reseller = selectedCustomer?.cfg.reseller;
  const isReseller = ExtPlatConfig.isReseller(reseller);
  const label = selectedCustomer?.cfg.label;
  const parentCId = selectedCustomer?.cfg.parentCId;
  const supportUri = reseller?.supportUri;
  const supportPhone = reseller?.supportPhone;

  return useMemo(() => {
    const facts = { isReseller, label, parentCId };
    return {
      brandName: BrandTokens.brandName(facts),
      operatorName: BrandTokens.operatorName(facts),
      supportContact: isReseller
        ? supportUri || supportPhone || 'your account administrator'
        : HELP_SUPPORT_EMAIL,
    };
  }, [isReseller, label, parentCId, supportUri, supportPhone]);
}
